import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Divider, Flex, Icon, SimpleGrid, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdQrCode2, MdRefresh, MdMiscellaneousServices } from "react-icons/md";
import Card from "horizon/components/card/Card";
import QRCodeSectionCard from "./QRCodeSectionCard";
import QRCodeResultSummary from "./QRCodeResultSummary";
import RegenerateConfirmDialog from "./RegenerateConfirmDialog";

/**
 * Builds the stats array for the services section in regeneration results.
 * @param {object} services - The services result object from the API.
 * @returns {Array<object>} Stats rows for QRCodeSectionCard.
 */
function buildRegenServiceStats(services) {
    const stats = [
        { label: "Total", value: services.total },
        { label: "Dispatched", value: services.dispatched, chipColor: "success" },
    ];
    if (services.errors > 0) {
        stats.push({ label: "Errors", value: services.errors, chipColor: "error" });
    }
    return stats;
}

/**
 * Displays a toast notification based on regeneration results.
 * @param {number} totalDispatched - Number of QR code tasks dispatched.
 * @param {number} totalErrors - Number of errors encountered.
 */
function showRegenToast(totalDispatched, totalErrors) {
    if (totalDispatched > 0) {
        toast.success(`${totalDispatched} QR code(s) being regenerated`);
    } else if (totalErrors > 0) {
        toast.error(`${totalErrors} error(s) during regeneration`);
    } else {
        toast("No QR codes to regenerate");
    }
}

/**
 * Renders the services section card for regeneration results.
 * @param {Object} props
 * @param {Object} props.result - API result with services data
 * @returns {React.ReactElement}
 */
function RegenSectionCards({ result }) {
    return (
        <SimpleGrid columns={{ base: 1 }} spacing="14px">
            <QRCodeSectionCard icon={MdMiscellaneousServices} iconColor="purple.500" title="Services"
                accent="purple.400" stats={buildRegenServiceStats(result.services)} errorMessage={result.services.error_message} />
        </SimpleGrid>
    );
}

/**
 * Success alert shown when regeneration tasks are dispatched.
 * @param {Object} props
 * @param {number} props.count - Number of tasks dispatched
 * @returns {React.ReactElement}
 */
function RegenSuccessAlert({ count }) {
    return (
        <Alert status="success" borderRadius="10px" mt="14px" alignItems="flex-start">
            <AlertIcon />
            <Box>
                <AlertTitle fontSize="sm">Regeneration started!</AlertTitle>
                <AlertDescription fontSize="xs">{count} Celery task(s) dispatched. Each service QR code is replaced on storage as its task completes.</AlertDescription>
            </Box>
        </Alert>
    );
}

/**
 * Renders the result panel after a full QR code regeneration.
 * @param {object} props
 * @param {object} props.result - API result with services data.
 * @returns {React.ReactElement} Result panel with summary and detail cards.
 */
function RegenResultPanel({ result }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const totalDispatched = result.services.dispatched || 0;
    const totalErrors = result.services.errors || 0;
    return (
        <Box p="16px" bg={bg} borderRadius="12px">
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="14px">Results</Text>
            <QRCodeResultSummary totalSuccess={totalDispatched} successLabel="Dispatched" totalErrors={totalErrors} />
            <RegenSectionCards result={result} />
            {totalDispatched > 0 && <RegenSuccessAlert count={totalDispatched} />}
        </Box>
    );
}

/**
 * Returns the error fallback result object used when the API call fails.
 * @param {string} errorMsg - The error message from the caught exception.
 * @returns {object} A result object with zeroed stats and the error message.
 */
function buildRegenErrorResult(errorMsg) {
    return {
        services: { total: 0, dispatched: 0, errors: 1, error_message: errorMsg },
    };
}

/**
 * Calls the API to regenerate all QR codes and updates state with results.
 * @param {Function} setConfirmOpen - Confirm dialog state setter
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setResult - Result state setter
 * @returns {Promise<void>}
 */
async function executeRegenerateAll(setConfirmOpen, setLoading, setResult) {
    setConfirmOpen(false);
    setLoading(true);
    setResult(null);
    try {
        const response = await axios.post('/admin/tools/regenerate_all_qr_codes');
        const data = response.data.qr_codes_regeneration;
        setResult(data);
        showRegenToast(data.services.dispatched || 0, data.services.errors || 0);
    } catch (error) {
        setResult(buildRegenErrorResult(error.response?.data || error.message));
        toast.error("Error regenerating QR codes");
    } finally {
        setLoading(false);
    }
}

/**
 * Header section for the regenerate all QR card.
 * @returns {React.ReactElement}
 */
function RegenCardHeader() {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Flex align="center" gap="12px" mb="12px">
                <Icon as={MdQrCode2} w="28px" h="28px" color="orange.400" />
                <Box>
                    <Text fontSize="lg" fontWeight="700" color={titleColor}>Regenerate All QR Codes</Text>
                    <Text fontSize="xs" color={subColor}>Force regeneration of every service QR code with the new design</Text>
                </Box>
            </Flex>
            <Divider mb="14px" />
        </Box>
    );
}

/**
 * Body section with warning alert, action button, and result panel.
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onOpen - Opens confirm dialog
 * @param {Object|null} props.result - Regeneration result
 * @returns {React.ReactElement}
 */
function RegenCardBody({ loading, onOpen, result }) {
    return (
        <Box>
            <Alert status="warning" borderRadius="12px" mb="16px" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <AlertTitle fontSize="sm">Heavy Operation</AlertTitle>
                    <AlertDescription fontSize="xs">Unlike &quot;Generate Missing&quot;, this action regenerates ALL existing service QR codes. Regeneration is asynchronous (Celery) and may take several minutes.</AlertDescription>
                </Box>
            </Alert>
            <Button w="100%" colorScheme="orange" leftIcon={<Icon as={MdRefresh} />} onClick={onOpen}
                isLoading={loading} loadingText="Regeneration in progress..." mb="16px">
                Regenerate All QR Codes
            </Button>
            {result && <RegenResultPanel result={result} />}
        </Box>
    );
}

/**
 * RegenerateAllQRCodes - Admin tool that forces regeneration of the QR code
 * of every non-test service, dispatching Celery tasks asynchronously.
 *
 * @returns {React.ReactElement} The regenerate all QR codes card with confirmation dialog.
 */
function RegenerateAllQRCodes() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    return (
        <Card mb="20px">
            <RegenCardHeader />
            <RegenCardBody loading={loading} onOpen={() => setConfirmOpen(true)} result={result} />
            <RegenerateConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
                onConfirm={() => executeRegenerateAll(setConfirmOpen, setLoading, setResult)} />
        </Card>
    );
}

export default RegenerateAllQRCodes;
